import { useEffect, useState } from "react";
import { Trophy, Crown, Medal, Users, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";

export default function PromoLeaderboard() {
  const [data, setData] = useState({ rows: [], my_rank: null });
  const [window, setWindow] = useState(30);
  const [loading, setLoading] = useState(true);
  const load = async (w = window) => {
    setLoading(true);
    try {
      const r = await api.get(`/aff-panel/leaderboard?window_days=${w}`);
      setData(r.data || { rows: [], my_rank: null });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const top3 = (data.rows || []).slice(0, 3);
  const rest = (data.rows || []).slice(3, 50);

  const podiumColor = (rank) => {
    if (rank === 1) return { c: "#a7f3d0", b: "rgba(34, 192, 115, 0.55)", icon: Crown };
    if (rank === 2) return { c: "#e2e8f0", b: "rgba(226,232,240,0.45)", icon: Medal };
    return { c: "#fca5a5", b: "rgba(244,180,49,0.45)", icon: Medal };
  };

  return (
    <div>
      <PageHeader
        eyebrow="COMPETITION"
        title="Affiliate Leaderboard"
        subtitle="See the top earners across Maxlive88 — climb the ranks and unlock perks."
        right={(
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((w) => (
              <button
                key={w}
                onClick={() => { setWindow(w); load(w); }}
                className={`px-3 h-9 rounded-lg text-[11px] font-bold uppercase tracking-[0.18em] ${window === w ? "p-btn-gold" : "p-btn-ghost"}`}
                data-testid={`leaderboard-window-${w}`}
              >
                {w}d
              </button>
            ))}
          </div>
        )}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard label="My Rank" value={data.my_rank ? `#${data.my_rank}` : "—"} icon={Trophy} accent="gold" raw />
        <StatCard label="Total Ranked" value={data.rows?.length || 0} icon={Users} accent="emerald" />
        <StatCard label="Window" value={`${window}d`} icon={TrendingUp} accent="amber" raw />
        <StatCard label="Competing For" value="VIP Status" raw icon={Crown} accent="slate" />
      </div>

      {/* Podium */}
      {top3.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[2, 1, 3].map((rk) => {
            const r = top3[rk - 1];
            if (!r) return <div key={rk} className="hidden md:block" />;
            const pc = podiumColor(rk);
            const Icon = pc.icon;
            const isFirst = rk === 1;
            return (
              <div
                key={r.account_id}
                className="p-glass p-card-hover relative"
                style={{
                  padding: isFirst ? 24 : 18,
                  marginTop: isFirst ? 0 : 12,
                  border: `1.5px solid ${pc.b}`,
                  background: `linear-gradient(180deg, ${pc.c}11, transparent 70%)`,
                  order: rk === 1 ? 2 : rk === 2 ? 1 : 3,
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg" style={{ background: pc.c + "22", border: `1.5px solid ${pc.b}`, color: pc.c }}>
                    <Icon size={22} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold">Rank #{rk}</div>
                    <div className="font-black text-white text-[16px] truncate">{r.username}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <div className="p-2 rounded-lg" style={{ background: "rgba(0,0,0,0.35)" }}>
                    <div className="text-white/45 text-[10px] uppercase tracking-[0.18em] font-bold">Net Loss</div>
                    <div className="font-black text-emerald-100">৳{(r.net_loss || 0).toLocaleString()}</div>
                  </div>
                  <div className="p-2 rounded-lg" style={{ background: "rgba(0,0,0,0.35)" }}>
                    <div className="text-white/45 text-[10px] uppercase tracking-[0.18em] font-bold">Commission</div>
                    <div className="font-black text-emerald-300">৳{(r.commission || 0).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="p-glass overflow-hidden">
        <div className="p-table overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Affiliate</th>
                <th>Deposits</th>
                <th>Net Loss</th>
                <th>Commission</th>
                <th>New Players</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center text-white/45 py-6">Loading…</td></tr>
              ) : rest.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-white/45 py-6">No more rankings.</td></tr>
              ) : rest.map((r, i) => (
                <tr key={r.account_id}>
                  <td>#{i + 4}</td>
                  <td className="font-bold text-white">{r.username}</td>
                  <td>৳{(r.deposits || 0).toLocaleString()}</td>
                  <td className="text-emerald-100 font-bold">৳{(r.net_loss || 0).toLocaleString()}</td>
                  <td className="text-emerald-300 font-bold">৳{(r.commission || 0).toLocaleString()}</td>
                  <td>{r.new_players}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
